import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { downloadBlob } from '../../lib/download'

interface JournalRental {
  id: string
  rentalNumber: string
  bike: string
  customerPhone: string
  customerName: string
  documentNumber: string
  manager: string
  pickupPoint: string
  returnPoint: string
  startAt: string
  endAt: string | null
  pricingMode: string
  basePrice: number
  totalPrice: number
  prepaymentAmount: number
  totalPaid: number
  totalRefunded: number
  balance: number
  actualPaid: number
  paymentMethod: string
}

const PRICING_LABELS: Record<string, string> = { hourly: '⏱ почасовая', daily: '🌙 суточная', override: '✏️ ручная' }
const fmt = (n: number) => n.toLocaleString('ru-RU') + ' ₽'
const fmtDate = (s: string) => {
  const d = new Date(s)
  return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

export default function JournalPage() {
  const today = new Date()
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const [from, setFrom] = useState(firstOfMonth.toISOString().slice(0, 10))
  const [to, setTo] = useState(today.toISOString().slice(0, 10))

  const { data, isLoading } = useQuery({
    queryKey: ['journal', from, to],
    queryFn: () => api.get(`/payments/journal?from=${from}&to=${to}T23:59:59`).then((r) => r.data),
  })

  /**
   * #2: mark a phone as a problem client. The note is surfaced to the manager as a
   * warning when that phone is entered on a new rental — it never blocks the rental.
   * Empty input clears the flag.
   */
  const flagCustomer = async (phone: string) => {
    let current = ''
    try {
      const res = await api.get('/customers/lookup', { params: { phone } })
      current = res.data?.customer?.flagNote ?? ''
    } catch { /* new customer — no flag yet */ }

    const note = window.prompt(
      `Проблемный клиент ${phone}\n\nКомментарий увидит менеджер при вводе этого телефона.\nПустое поле — снять пометку.`,
      current,
    )
    if (note === null) return // cancelled

    try {
      await api.post('/customers/flag', { phone, note: note.trim() ? note.trim() : null })
      alert(note.trim() ? 'Клиент помечен' : 'Пометка снята')
    } catch {
      alert('Не удалось сохранить пометку')
    }
  }

  // #2: manual blacklist entry — paste a phone by hand, see/edit its current comment.
  const [blOpen, setBlOpen] = useState(false)
  const [blPhone, setBlPhone] = useState('')
  const [blNote, setBlNote] = useState('')
  const [blLoaded, setBlLoaded] = useState<null | 'has' | 'none'>(null)
  const openBlacklist = () => { setBlPhone(''); setBlNote(''); setBlLoaded(null); setBlOpen(true) }
  const blLookup = async () => {
    if (!blPhone.trim()) return
    try {
      const res = await api.get('/customers/lookup', { params: { phone: blPhone.trim() } })
      const note = res.data?.customer?.flagNote ?? ''
      setBlNote(note); setBlLoaded(note ? 'has' : 'none')
    } catch { setBlNote(''); setBlLoaded('none') }
  }
  const blSave = async () => {
    if (!blPhone.trim()) { alert('Введите телефон'); return }
    try {
      await api.post('/customers/flag', { phone: blPhone.trim(), note: blNote.trim() ? blNote.trim() : null })
      alert(blNote.trim() ? 'Клиент помечен' : 'Пометка снята')
      setBlOpen(false)
    } catch { alert('Не удалось сохранить пометку') }
  }

  const rentals: JournalRental[] = data?.rentals ?? []
  const summary = data?.summary as
    | {
        count: number; shown: number
        actualRevenue: number; avgDurationMinutes: number; refundCount: number
        modelLeader: string; modelLoser: string
      }
    | undefined

  const handleCsvExport = async () => {
    const res = await api.get(`/payments/journal/csv?from=${from}&to=${to}T23:59:59`, { responseType: 'blob' })
    await downloadBlob(res.data, `journal-${from}-${to}.csv`)
  }

  // Owner-friendly metrics come from the server summary (whole period).
  const periodCount = summary?.count ?? rentals.length
  const shownCount = summary?.shown ?? rentals.length
  const isCapped = periodCount > shownCount
  const actualRevenue = summary?.actualRevenue ?? rentals.reduce((s, r) => s + r.actualPaid, 0)
  const refundCount = summary?.refundCount ?? 0
  const modelLeader = summary?.modelLeader ?? '—'
  const modelLoser = summary?.modelLoser ?? '—'
  const avgDurationMinutes = summary?.avgDurationMinutes ?? 0

  const formatAvgDuration = (minutes: number): string => {
    if (minutes < 1) return '—'
    if (minutes < 60) return `${Math.round(minutes)} мин`
    const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60)
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Журнал аренд</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {periodCount} аренд за период
            {isCapped && <span className="text-amber-600"> · в таблице показаны первые {shownCount}, итоги — за весь период</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openBlacklist}
            className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
          >
            ⚠ Чёрный список
          </button>
          <button
            onClick={handleCsvExport}
            className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
          >
            📥 CSV экспорт
          </button>
        </div>
      </div>

      {/* Date filter */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">С</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">По</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Totals — owner-friendly metrics (#5.3) */}
      {periodCount > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            ['Кол-во аренд', String(periodCount), false],
            ['Ср. время аренды', formatAvgDuration(avgDurationMinutes), false],
            ['Выручка (факт)', fmt(actualRevenue), false],
            ['Возвратов (проблемных)', `${refundCount} шт`, false],
            ['Лидер по арендам', modelLeader, true],
            ['Аутсайдер по арендам', modelLoser, true],
          ].map(([label, val, small]) => (
            <div key={String(label)} className="bg-white border border-gray-200 rounded-2xl p-4">
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`font-bold text-gray-900 mt-1 ${small ? 'text-sm break-words' : 'text-xl'}`}>{val}</p>
            </div>
          ))}
        </div>
      )}

      {isLoading && <p className="text-gray-400 text-center py-12">Загрузка...</p>}

      {!isLoading && rentals.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p>Нет аренд за выбранный период</p>
        </div>
      )}

      {rentals.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3 text-left">№</th>
                  <th className="px-4 py-3 text-left">Велосипед</th>
                  <th className="px-4 py-3 text-left">Клиент</th>
                  <th className="px-4 py-3 text-left">Менеджер</th>
                  <th className="px-4 py-3 text-left">Точка</th>
                  <th className="px-4 py-3 text-left">Начало</th>
                  <th className="px-4 py-3 text-left">Конец</th>
                  <th className="px-4 py-3 text-left">Тариф</th>
                  <th
                    className="px-4 py-3 text-right normal-case"
                    title="Фактически полученные деньги по аренде (предоплаты + доплаты − возвраты)"
                  >
                    <span className="block uppercase text-xs text-gray-500">Сумма</span>
                    <span className="block text-[10px] font-normal text-gray-400 mt-0.5">по факту оплат</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rentals.map((r) => {
                  const isActive = !r.endAt
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${isActive ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">{r.rentalNumber || '—'}</td>
                      <td className="px-4 py-3 font-medium">
                        {r.bike}
                        {isActive && <span className="ml-1 text-[10px] text-blue-500 font-normal">активна</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-1.5">
                          <div className="min-w-0">
                            {r.customerName ? (
                              <>
                                <p>{r.customerName}</p>
                                <p className="text-xs text-gray-400">{r.customerPhone}</p>
                              </>
                            ) : (
                              <p>{r.customerPhone}</p>
                            )}
                          </div>
                          {/* #2: flag a problem client — the note is shown to the manager
                              when this phone is entered on a new rental (never blocks). */}
                          <button
                            type="button"
                            title="Пометить проблемного клиента"
                            onClick={() => flagCustomer(r.customerPhone)}
                            className="shrink-0 text-gray-300 hover:text-amber-500 text-sm leading-none px-1"
                          >
                            ⚠
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.manager}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <p className="text-xs">{r.pickupPoint}</p>
                        {r.returnPoint !== r.pickupPoint && <p className="text-xs text-orange-500">→ {r.returnPoint}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(r.startAt)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{r.endAt ? fmtDate(r.endAt) : '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {PRICING_LABELS[r.pricingMode] ?? r.pricingMode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(r.actualPaid)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* #2: manual blacklist modal — paste a phone, see/edit its comment */}
      {blOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBlOpen(false)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold">Чёрный список — пометить клиента</h3>
              <button onClick={() => setBlOpen(false)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Телефон</label>
              <div className="flex gap-2">
                <input
                  value={blPhone}
                  onChange={(e) => { setBlPhone(e.target.value); setBlLoaded(null) }}
                  onBlur={blLookup}
                  placeholder="+7…"
                  className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
                <button onClick={blLookup} className="border border-gray-300 px-3 rounded-xl text-sm">Найти</button>
              </div>
              {blLoaded === 'has' && <p className="text-[11px] text-amber-700 mt-1">Уже в списке — комментарий загружен, можно изменить.</p>}
              {blLoaded === 'none' && <p className="text-[11px] text-gray-400 mt-1">Пометки пока нет — заполните комментарий и сохраните.</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Комментарий (увидит менеджер при вводе телефона)</label>
              <textarea
                value={blNote}
                onChange={(e) => setBlNote(e.target.value)}
                rows={3}
                placeholder="Напр.: возвращал велик битым, задерживал оплату"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1">Пустой комментарий — снять пометку.</p>
            </div>
            <button onClick={blSave} className="w-full bg-brand-600 text-white font-semibold py-2.5 rounded-xl text-sm">Сохранить</button>
          </div>
        </div>
      )}
    </div>
  )
}
